import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { selectActiveCompanyId } from "../../redux/features/user/selectors/userState.selectors";
import { ChatTarget, getChatTargetKey, OpenChat } from "./chatDock.types";

const MAX_OPEN_CHATS = 5;

export interface OpenChatMeta {
  companyName?: string;
  chatName?: string;
  focusMessageUid?: string | null;
  focusMessageText?: string | null;
  minimized?: boolean;
}

interface ChatDockContextValue {
  openChats: OpenChat[];
  focusedKey: string | null;
  openChat: (target: ChatTarget, meta?: OpenChatMeta) => string;
  closeChat: (key: string) => void;
  closeAllChats: () => void;
  saveOpenChatsForCompany: (companyId?: number | string | null) => void;
  restoreOpenChatsForCompany: (companyId?: number | string | null) => void;
  toggleMinimize: (key: string) => void;
  focusChat: (key: string) => void;
  clearMessageFocus: (key: string) => void;
  markRead: (key: string) => void;
  bumpUnread: (key: string) => void;
  isChatOpen: (key: string) => boolean;
  getChatKey: (target: ChatTarget) => string;
}

const ChatDockContext = createContext<ChatDockContextValue | null>(null);

const getCompanySnapshotKey = (companyId?: number | string | null) => (companyId == null ? null : String(companyId));

export const ChatDockProvider = ({ children }: { children: ReactNode }) => {
  const [openChats, setOpenChats] = useState<OpenChat[]>([]);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const openChatsSnapshotsRef = useRef<Record<string, { openChats: OpenChat[]; focusedKey: string | null }>>({});
  const activeCompanyId = useSelector(selectActiveCompanyId);

  const scopeTargetToCompany = useCallback(
    (target: ChatTarget): ChatTarget => {
      if (target.type === "internal") {
        return { ...target, companyId: target.companyId ?? target.contact?.companyId ?? activeCompanyId };
      }

      if (target.type === "sms" || target.type === "group") {
        return { ...target, companyId: target.companyId ?? activeCompanyId };
      }

      return target;
    },
    [activeCompanyId],
  );

  const getChatKey = useCallback((target: ChatTarget) => getChatTargetKey(scopeTargetToCompany(target)), [scopeTargetToCompany]);

  const openChat = useCallback((rawTarget: ChatTarget, meta?: OpenChatMeta) => {
    const target = scopeTargetToCompany(rawTarget);
    const key = getChatTargetKey(target);
    const now = Date.now();

    setOpenChats((current) => {
      const existing = current.find((chat) => chat.key === key);

      if (existing) {
        return current.map((chat) =>
          chat.key === key
            ? {
                ...chat,
                target: { ...chat.target, ...target },
                companyName: meta?.companyName ?? chat.companyName,
                chatName: meta?.chatName ?? chat.chatName,
                focusMessageUid: meta?.focusMessageUid ?? null,
                focusMessageText: meta?.focusMessageText ?? null,
                minimized: false,
                unreadCount: 0,
                lastActivityAt: now,
              }
            : chat,
        );
      }

      const next: OpenChat = {
        key,
        target,
        companyName: meta?.companyName ?? "",
        chatName: meta?.chatName ?? "",
        focusMessageUid: meta?.focusMessageUid ?? null,
        focusMessageText: meta?.focusMessageText ?? null,
        minimized: meta?.minimized ?? false,
        unreadCount: 0,
        openedAt: now,
        lastActivityAt: now,
      };

      const withNew = [...current, next];

      if (withNew.length <= MAX_OPEN_CHATS) return withNew;

      const stalest = withNew
        .filter((chat) => chat.key !== key)
        .reduce((oldest, chat) => (chat.lastActivityAt < oldest.lastActivityAt ? chat : oldest));

      return withNew.filter((chat) => chat.key !== stalest.key);
    });

    setFocusedKey(key);

    return key;
  }, [scopeTargetToCompany]);

  const closeChat = useCallback((key: string) => {
    setOpenChats((current) => current.filter((chat) => chat.key !== key));
    setFocusedKey((current) => (current === key ? null : current));
  }, []);

  const closeAllChats = useCallback(() => {
    setOpenChats([]);
    setFocusedKey(null);
  }, []);

  const saveOpenChatsForCompany = useCallback(
    (companyId?: number | string | null) => {
      const snapshotKey = getCompanySnapshotKey(companyId);
      if (!snapshotKey) return;

      if (openChats.length === 0) {
        delete openChatsSnapshotsRef.current[snapshotKey];
        return;
      }

      openChatsSnapshotsRef.current[snapshotKey] = {
        openChats: openChats.map((chat) => ({ ...chat })),
        focusedKey,
      };
    },
    [focusedKey, openChats],
  );

  const restoreOpenChatsForCompany = useCallback((companyId?: number | string | null) => {
    const snapshotKey = getCompanySnapshotKey(companyId);
    const snapshot = snapshotKey ? openChatsSnapshotsRef.current[snapshotKey] : null;

    if (!snapshot) {
      setOpenChats([]);
      setFocusedKey(null);
      return;
    }

    setOpenChats(snapshot.openChats.map((chat) => ({ ...chat })));
    setFocusedKey(snapshot.focusedKey);
  }, []);

  const toggleMinimize = useCallback((key: string) => {
    let willMinimize = false;

    setOpenChats((current) =>
      current.map((chat) => {
        if (chat.key !== key) return chat;

        willMinimize = !chat.minimized;

        return {
          ...chat,
          minimized: willMinimize,
          unreadCount: willMinimize ? chat.unreadCount : 0,
          lastActivityAt: Date.now(),
        };
      }),
    );

    setFocusedKey((current) => (willMinimize ? (current === key ? null : current) : key));
  }, []);

  const focusChat = useCallback((key: string) => {
    setFocusedKey(key);
    setOpenChats((current) =>
      current.map((chat) => (chat.key === key ? { ...chat, minimized: false, unreadCount: 0, lastActivityAt: Date.now() } : chat)),
    );
  }, []);

  const clearMessageFocus = useCallback((key: string) => {
    setOpenChats((current) =>
      current.map((chat) => (chat.key === key ? { ...chat, focusMessageUid: null, focusMessageText: null } : chat)),
    );
  }, []);

  const markRead = useCallback((key: string) => {
    setOpenChats((current) => current.map((chat) => (chat.key === key ? { ...chat, unreadCount: 0 } : chat)));
  }, []);

  const bumpUnread = useCallback((key: string) => {
    setOpenChats((current) =>
      current.map((chat) =>
        chat.key === key ? { ...chat, unreadCount: chat.unreadCount + 1, lastActivityAt: Date.now() } : chat,
      ),
    );
  }, []);

  const isChatOpen = useCallback((key: string) => openChats.some((chat) => chat.key === key), [openChats]);

  const value = useMemo<ChatDockContextValue>(
    () => ({
      openChats,
      focusedKey,
      openChat,
      closeChat,
      closeAllChats,
      saveOpenChatsForCompany,
      restoreOpenChatsForCompany,
      toggleMinimize,
      focusChat,
      clearMessageFocus,
      markRead,
      bumpUnread,
      isChatOpen,
      getChatKey,
    }),
    [
      openChats,
      focusedKey,
      openChat,
      closeChat,
      closeAllChats,
      saveOpenChatsForCompany,
      restoreOpenChatsForCompany,
      toggleMinimize,
      focusChat,
      clearMessageFocus,
      markRead,
      bumpUnread,
      isChatOpen,
      getChatKey,
    ],
  );

  return <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>;
};

export const useChatDock = (): ChatDockContextValue => {
  const context = useContext(ChatDockContext);

  if (!context) throw new Error("useChatDock must be used inside a ChatDockProvider");

  return context;
};

export const useOptionalChatDock = (): ChatDockContextValue | null => useContext(ChatDockContext);
